import { OpportunityParticipant } from "@entities";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import dayjs from "dayjs";

const toValidDate = (value: unknown): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(date.getTime()) ? date : null;
};

const interestedCreatedAtFilter: HyperTableFilter<OpportunityParticipant> = {
  id: "createdAt",
  icon: "date",
  caption: "תאריך התעניינות",
  renderFilter: ({ onSave, meta }) => {
    const startDate = toValidDate(meta.startDate);
    const endDate = toValidDate(meta.endDate);

    const buildFilter = (start: Date | null, end: Date | null) => {
      if (!start && !end) return {};
      return OpportunityParticipant.hyperFilter({
        createdAt: {
          ...(start ? { $gte: dayjs(start).startOf("day").toDate() } : {}),
          ...(end ? { $lte: dayjs(end).endOf("day").toDate() } : {}),
        },
      });
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
        adapterLocale="he"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <DatePicker
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) => {
                const next = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(next, endDate),
                  meta: { startDate: next, endDate },
                });
              }}
              label="מתאריך"
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              value={endDate ? dayjs(endDate) : null}
              onChange={(date) => {
                const next = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(startDate, next),
                  meta: { startDate, endDate: next },
                });
              }}
              label="עד תאריך"
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small" } }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: "flex-start" }}
            onClick={() =>
              onSave({ value: {}, meta: { startDate: null, endDate: null } })
            }
          >
            נקה סינון
          </Button>
        </Box>
      </LocalizationProvider>
    );
  },
};

const filters: HyperTableFilter<OpportunityParticipant>[] = [
  { fieldName: "association.user.name", id: "name", caption: "שם" },
  createPhoneNumberFilter(OpportunityParticipant, {
    fieldName: "association.user.phoneNumber",
    id: "phoneNumber",
    caption: "טלפון",
  }),
  interestedCreatedAtFilter,
  { fieldName: "association.user.gender", id: "gender", caption: "מגדר" },
  {
    id: "groupOrIndividual",
    icon: "literal",
    caption: "קבוצה או יחיד",
    renderFilter: ({ onSave, meta }) => {
      const value = (meta.value as string) ?? "";
      return (
        <Box sx={{ p: 1, minWidth: 180 }}>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={value}
            onChange={(event) => {
              const next = event.target.value;
              onSave({
                value:
                  next === "group"
                    ? OpportunityParticipant.hyperFilter({
                        groupId: { $not: "" },
                      })
                    : next === "individual"
                      ? OpportunityParticipant.isIndividualParticipant()
                      : {},
                meta: {
                  value: next,
                  text: next === "group" ? "קבוצה" : next === "individual" ? "יחיד" : "",
                },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            <MenuItem value="group">קבוצה</MenuItem>
            <MenuItem value="individual">יחיד</MenuItem>
          </Select>
        </Box>
      );
    },
  },
];

export default filters;
